import React, { useState, useEffect, useCallback } from 'react';
import { Key, Plus, Trash2, Loader2, Copy, Check, Eye, EyeOff } from 'lucide-react';
import { authAPI, isAnyAdmin } from '../lib/auth';

export default function APIKeysPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: [], expires_days: 90 });
  const [error, setError] = useState(null);
  const admin = isAnyAdmin();

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.listApiKeys();
      setKeys(res.data.keys || []);
    } catch {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const res = await authAPI.createApiKey(form);
      setNewKeyResult(res.data.key);
      setShowCreate(false);
      setForm({ name: '', scopes: [], expires_days: 90 });
      loadKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await authAPI.revokeApiKey(id);
      loadKeys();
    } catch {
      setError('Failed to revoke key');
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const AVAILABLE_SCOPES = [
    'read:merchants', 'write:merchants', 'read:reports', 'write:reports',
    'read:classifications', 'read:analytics', 'webhook:receive',
  ];

  const toggleScope = (scope) => {
    setForm(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Key className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
        </div>
        {admin && (
          <button
            onClick={() => { setShowCreate(true); setNewKeyResult(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
          >
            <Plus className="w-4 h-4" /> Generate Key
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>}

      {/* New key reveal */}
      {newKeyResult && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
          <div className="text-sm font-medium text-amber-800">New API Key Created — copy it now, it won't be shown again</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white px-3 py-2 rounded border border-amber-200 text-sm font-mono break-all">
              {newKeyResult.raw_key}
            </code>
            <button onClick={() => copyKey(newKeyResult.raw_key)} className="p-2 rounded-lg hover:bg-amber-100">
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-amber-600" />}
            </button>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Generate New API Key</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              required
              placeholder="e.g. Production Integration"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scopes</label>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_SCOPES.map(scope => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => toggleScope(scope)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    form.scopes.includes(scope)
                      ? 'bg-amber-100 border-amber-300 text-amber-800'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expires (days)</label>
            <input
              type="number"
              value={form.expires_days}
              onChange={e => setForm(p => ({ ...p, expires_days: parseInt(e.target.value) || 90 }))}
              min={1}
              max={365}
              className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={creating} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Prefix</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Scopes</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Expires</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Last Used</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {keys.map(k => (
                  <tr key={k.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{k.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-500">{k.key_prefix}...</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        {(Array.isArray(k.scopes) ? k.scopes : JSON.parse(k.scopes || '[]')).map(s => (
                          <span key={s} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        k.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {k.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {k.expires_at ? new Date(k.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      {k.is_active && admin && (
                        <button
                          onClick={() => handleRevoke(k.id)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                          title="Revoke"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {keys.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400">No API keys</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
